import {
  Box,
  Skeleton,
  Text,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import {
  CatalogTrainingRequestFragment,
  GetCatalogTrainingsDocument,
  GetCatalogTrainingsQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { WizardData } from 'modules/trainings/components/Shared/WizardComponent/types'

type Props = {
  amplitudeEventNext: string
}

interface Training {
  id: string
  name: string
  categories?: Array<{ id: string; name: string }>
  competencies?: Array<{ id: string; name: string }>
}

const EntityTrainingSelector = ({
  onTrainingSelected,
  selectedTraining,
}: {
  onTrainingSelected: (training: Training | null) => void
  selectedTraining: Training | null
}) => {
  const extractConnection = (data: GetCatalogTrainingsQuery) => {
    return data.trainings.trainingsConnection
  }

  const renderEntity = (entity: CatalogTrainingRequestFragment) => (
    <Box gap="s8" flexDirection="row" alignItems="center">
      <Text.Normal size="300">{entity.name}</Text.Normal>
    </Box>
  )

  const renderValue = (value: CatalogTrainingRequestFragment | null) => (
    <Text.Normal ellipsis>{value ? value.name : 'Select training...'}</Text.Normal>
  )

  const renderSkeleton = () => (
    <Box gap="s8" flexDirection="row" alignItems="center">
      <Skeleton width="s240" height="s32" />
    </Box>
  )

  return (
    <Box width="s600" position="relative">
      <EntitySelector
        document={GetCatalogTrainingsDocument}
        variables={{ catalog: true }}
        extractConnection={extractConnection}
        renderEntity={renderEntity}
        renderValue={renderValue}
        renderSkeleton={renderSkeleton}
        value={selectedTraining}
        onChange={(value: CatalogTrainingRequestFragment | null) => onTrainingSelected(value)}
        width="s600"
        label={i18n.t('trainings.training_requests.create.training')}
        mandatory={false}
        disabled={false}
        hideSearchIcon
      />
    </Box>
  )
}

export const TrainingSelectorStep = ({
  data,
  onDataChanged,
  amplitudeEventNext,
}: WizardStepRenderParams<WizardData> & Props) => {
  useOnNext(() => {
    track(amplitudeEventNext, {
      from: 'my_request',
    })
  })

  const selectedTraining = data.trainingId
    ? { id: data.trainingId, name: data.trainingName || '' }
    : null

  const handleTrainingSelected = (training: Training | null) => {
    if (training) {
      onDataChanged({
        ...data,
        trainingId: String(training.id),
        trainingName: training.name,
      })
    } else {
      onDataChanged({
        ...data,
        trainingId: undefined,
        trainingName: undefined,
      })
    }
  }

  return (
    <Box width="s600" gap="s24">
      <Box gap="s16">
        <Text size="400" weight="medium" color="grey1000">
          {i18n.t('trainings.training_requests.create.training_step_title')}
        </Text>
        <Text size="300" weight="normal" color="grey1000">
          {i18n.t('trainings.training_requests.create.training_step_description')}
        </Text>
      </Box>
      <EntityTrainingSelector
        onTrainingSelected={handleTrainingSelected}
        selectedTraining={selectedTraining}
      />
    </Box>
  )
}
